import {
  DismissReason,
  GiveBackResult,
  LinkKind,
  TakeOverResult,
  TransportItem,
} from './types';

const TZ = 'America/Toronto';

/** "2026-10-21" in Toronto, for comparing calendar days. */
function torontoDay(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: TZ });
}

/**
 * "07:41" when it is today in Toronto, otherwise "Oct 20 07:41". The queue only
 * holds the last three days, so a date is all the context an older call needs.
 */
export function formatCallTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const time = d.toLocaleTimeString('en-CA', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  if (torontoDay(d) === torontoDay(now)) return time;
  const day = d.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric' });
  return `${day} ${time}`;
}

const PLACE_LABELS: Record<string, string> = {
  hospital: 'Hospital',
  chsld: 'CHSLD',
  home: 'Home',
  coroner: 'Coroner',
};

/** "CHSLD Saint-Laurent", or just "CHSLD" / the name, or null. */
export function placeLabel(type: string | null, name: string | null): string | null {
  const t = type ? PLACE_LABELS[type] || type : null;
  const n = name?.trim() || null;
  if (t && n) return n.toLowerCase().includes(t.toLowerCase()) ? n : `${t} · ${n}`;
  return t || n;
}

export function waitingForLabel(w: TransportItem['waitingFor']): string {
  return w === 'release_call' ? 'Waiting for release call' : 'Waiting for SP-3';
}

export function regionCode(r: TransportItem['region']): string | null {
  if (r === 'quebec') return 'QC';
  if (r === 'ontario') return 'ON';
  return null;
}

/** The one-line description shared by the strip row and the case banner. */
export function rowDetail(item: TransportItem, now: Date = new Date()): string {
  return [
    formatCallTime(item.createdAt, now),
    placeLabel(item.placeType, item.placeName),
    item.state === 'waiting' ? waitingForLabel(item.waitingFor) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function takenOverLine(item: TransportItem, now: Date = new Date()): string {
  const who = item.takenOverBy || 'staff';
  return `Taken over by ${who} · ${formatCallTime(item.takenOverAt, now)}`;
}

export function isUnlinked(item: Pick<TransportItem, 'funeralId' | 'leadId'>): boolean {
  return !item.funeralId && !item.leadId;
}

/** Toast text for a take-over attempt. `ok` = it worked or nothing to do. */
export function takeOverMessage(
  r: { result: TakeOverResult; at?: string | null; by?: string | null; destination?: string | null },
  now: Date = new Date(),
): { text: string; ok: boolean } {
  const at = r.at ? ` at ${formatCallTime(r.at, now)}` : '';
  switch (r.result) {
    case 'taken':
      return { text: "Taken over — Kewix won't dispatch this one", ok: true };
    case 'already_taken':
      return { text: `${r.by || 'Someone'} already took this over${at}`, ok: true };
    case 'already_dispatched':
      return {
        text: `Kewix already dispatched${at}${r.destination ? ` to ${r.destination}` : ''} — nothing changed`,
        ok: false,
      };
    case 'closed':
      return { text: 'This call was dismissed as not a case', ok: false };
    default:
      return { text: "Couldn't find this Kewix call", ok: false };
  }
}

export function giveBackMessage(r: GiveBackResult): { text: string; ok: boolean } {
  switch (r) {
    case 'given_back':
      return { text: "Given back — it's in Kewix's transport queue again", ok: true };
    case 'not_taken':
      return { text: 'Already back with Kewix', ok: true };
    case 'already_dispatched':
      return { text: 'Kewix already dispatched this one', ok: false };
    default:
      return { text: "Couldn't find this Kewix call", ok: false };
  }
}

export const DISMISS_REASONS: { value: DismissReason; label: string }[] = [
  { value: 'wrong_number', label: 'Wrong number' },
  { value: 'other_funeral_home', label: 'Another funeral home' },
  { value: 'duplicate', label: 'Duplicate of another call' },
  { value: 'not_a_death', label: 'Not a death' },
  { value: 'other', label: 'Other' },
];

/** "Other" needs a few words, every other reason stands on its own. */
export function dismissReady(reason: DismissReason | '', note: string): boolean {
  if (!reason) return false;
  return reason !== 'other' || note.trim().length > 0;
}

export function linkTargetLabel(kind: LinkKind, id: number | string): string {
  return kind === 'funeral' ? `case #${id}` : 'lead';
}
